import { Provider } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { State, process } from '@progress/kendo-data-query';

import { DataAccessService, EntityDataService } from '@pmis3/data-access';
import { IModelBase } from '@pmis3/utils';
import { UiRouteService } from '../../ui-route.service';
import { BaseEntityListStore, EntityListDataState } from './';

export class EntityListClientSideStore<T extends IModelBase> extends BaseEntityListStore<T, EntityListDataState<T>> {
    // Static storage for cached data per entity type
    private static entityCachedData: Map<string, IModelBase[]> = new Map();

    readonly allData$: Observable<T[]> = this.select((s) => s.allData);

    constructor(protected entityService: EntityDataService, 
        entityType: string,
    ) {
        // Get existing cached data for this entity type or create defaults
        const existingCachedData = (EntityListClientSideStore.entityCachedData.get(entityType) || []) as T[];
        
        super(entityType, { 
            gridData: { data: [], total: 0 }, 
            loading: false, 
            dataState: { skip: 0, take: 10 },
            entityType: entityType,
            allData: existingCachedData
        });

        // If we have cached data, apply client-side processing immediately
        if (existingCachedData.length > 0) {
            this.applyClientSideProcessing(existingCachedData, this.getCurrentDataState());
        }
    }

    /**
     * Loads all entities from the backend and caches them for client-side processing
     */
    private readonly _loadEntities = this.effect<void>((trigger$) =>
        trigger$.pipe(
            tap(() => this.setLoading(true)),
            tap(() => this.setError(undefined)),
            switchMap(() => {
                // Fetch all data without pagination by setting a very large page size
                return this.entityService.get({
                    skip: 0,
                    take: 999999,
                }).pipe(
                    tap((result) => {
                        const allData = result.data || [];
                        
                        // Cache the data for this entity type
                        EntityListClientSideStore.entityCachedData.set(this.entityType, allData);
                        
                        // Update state with all data
                        this.patchState({ 
                            allData: allData
                        });
                        this.setLoading(false);
                        
                        // Apply client-side processing with current dataState
                        const currentState = this.get();
                        this.applyClientSideProcessing(allData, currentState.dataState);
                    }),
                    catchError((error: Error) => {
                        this.setLoading(false);
                        this.setError(error.message);
                        return of();
                    })
                );
            })
        )
    );

    /**
     * Implementation of abstract loadData method
     */
    loadData(): void {
        this._loadEntities();
    }

    /**
     * Updates the data state and applies client-side processing
     * Overrides the base implementation to add client-side processing
     */
    override readonly setDataState = this.updater((state, dataState: State) => {
        // Persist the dataState for this entity type (same as base class)
        BaseEntityListStore.entityDataStates.set(state.entityType, dataState);
        
        // Apply client-side processing with new dataState
        this.applyClientSideProcessing(state.allData, dataState);
        
        return { ...state, dataState };
    });

    /**
     * Applies client-side filtering, sorting, and paging to the cached data
     */
    private applyClientSideProcessing(allData: T[], dataState: State): void {
        if (allData.length === 0) {
            this.setGridData({ data: [], total: 0 });
            return;
        }

        // Use Kendo's process function to handle filtering, sorting, and paging
        const processedResult = process(allData, dataState);
        
        this.setGridData({
            data: processedResult.data,
            total: processedResult.total || allData.length
        });
    }

    /**
     * Clears the stored dataState and cached data for this entity type
     */
    override clearDataState(): void {
        super.clearDataState();
        EntityListClientSideStore.entityCachedData.set(this.entityType, []);
        this.patchState({ 
            allData: []
        });
    }

    /**
     * Creates a provider configuration for EntityListClientSideStore with the specified entity type.
     * 
     * @param entityType - The entity type identifier (e.g., 'l' for location, 'o' for organization, 'u' for user, 'v' for vessel).
     * @returns An array of providers for EntityListClientSideStore.
     */
    static createProviders(entityType: string): Provider[] {
        return [
            {
                provide: EntityListClientSideStore,
                useFactory: (das: DataAccessService) => 
                    new EntityListClientSideStore(new EntityDataService(entityType, das), entityType),
                deps: [DataAccessService],
            },
            UiRouteService
        ];
    }
}